/**
 * HeroName
 * ─────────────────────────────────────────────────────────────
 * Splits "Sean" + "Burles" into per-letter <span>s and applies an
 * inverse parallax to each one: as the page scrolls down, each letter
 * shoots UP faster than the page — at its own per-letter speed. The
 * wordmark fans out vertically as the hero scrolls out of view.
 *
 *   - target = live page scroll position (Lenis-driven, native fallback).
 *   - Each letter's Y offset = −target × CHAR_LEAD_FACTORS[i]
 *   - Higher factor = faster lead = letter shoots up sooner.
 *   - At scroll=0 every letter is at offset 0 — wordmark looks normal.
 *   - Cap prevents extreme drift on long pages, but the hero is already
 *     out of view well before any letter hits the cap.
 *
 * Doesn't touch the .name__solid / .name__ghost wrappers' transforms
 * (the CSS theme tokens on those still apply, and the reveal tween in
 * main.js still animates them). Per-letter transforms compose with the
 * wrapper transform via the normal CSS transform stack.
 *
 * Public API:
 *   destroy()
 */

// Per-letter lead factor. Letter's Y translate = −scrollY × factor.
// Higher = letter moves up faster than the page during scroll-down.
//
// 10 entries to match "Sean" (4) + "Burles" (6). Hand-tuned so every
// letter has a distinct speed, with no obvious pattern. The slowest
// letters (factor ~0.20) drift just enough to read as parallax; the
// fastest (factor ~0.75) shoot up dramatically.
//
// Tuning guide (at a typical 600px scroll, hero nearly out of view):
//   0.20 →   120px lead   (subtle drift)
//   0.40 →   240px lead   (clearly faster than page)
//   0.70 →   420px lead   (rocket — letter is gone before hero is)
const CHAR_LEAD_FACTORS = [
  // S      e      a      n        B      u      r      l      e      s
  0.35, 0.60, 0.25, 0.70,   0.30, 0.55, 0.20, 0.75, 0.40, 0.50,
];

// Hard cap on per-letter offset. The hero is ~100svh tall, so by the
// time scroll hits ~700px the wordmark is already off-screen and any
// further lead is invisible. 400px is a generous ceiling that still
// keeps the math sane on very tall pages.
const CHAR_LEAD_MAX_PX = 400;

// Letter-treatment shapes: f(t, i, n) → roughly −1…1 across the word (t = 0
// first letter … 1 last). Centred + normalised before use, so only the
// silhouette matters here. Applied only after the first theme change.
const SHAPES = {
  swell:   (t) => t * 2 - 1,                                   // thin → FAT
  taper:   (t) => 1 - t * 2,                                   // FAT → thin
  pinch:   (t) => Math.abs(t * 2 - 1) * 2 - 1,                  // FAT thin FAT
  bulge:   (t) => 1 - Math.abs(t * 2 - 1) * 2,                  // thin FAT thin
  zigzag:  (t, i) => (i % 2 ? 1 : -1),                          // FAT thin FAT thin
  stomp:   (t, i) => (i % 3 === 0 ? 1 : -0.5),                  // FAT thin thin FAT
  wave:    (t) => Math.sin(t * Math.PI * 2 - Math.PI / 2),       // thin FAT thin (softer)
  skyline: (t, i, n) => [0.9, -0.7, 0.3, -1, 0.6, -0.2, 1, -0.5][i % 8] * (1 - 0.15 * (n > 5)), // fixed "random" poster stack
};

export class HeroName {
  constructor(root = document.querySelector('.name')) {
    if (!root) return;
    this.root = root;

    this._chars = [];
    this._scrollTarget = (typeof window !== 'undefined' && window.scrollY) || 0;

    this._split();
    // Per-letter scroll lag is a transform write on every letter, every frame.
    // On a phone that fights the same scroll the cards are trying to follow.
    this._coarse = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
    if (!this._coarse) this._bindScroll();
    // First paint is the plain wordmark (the theme's normal weights). The
    // per-letter poster treatment is a treat that only starts once the
    // visitor rolls the dice / changes theme.
    this._armed = false;
    this._styleLetters();
    this._themeObs = new MutationObserver(() => {
      this._armed = true;
      this._styleLetters();
      // a cached fit for this theme+viewport applies NOW, so the tracking
      // tweens together with the axis morph instead of arriving late
      const cached = this._fitCache?.[this._fitKey()];
      const ghost = this.root.querySelector('.name__ghost');
      if (cached != null && ghost) {
        ghost.style.letterSpacing = cached.ls;
        if (cached.wdth) ghost.style.setProperty('--fit-wdth', cached.wdth); else ghost.style.removeProperty('--fit-wdth');
      } else {
        this._fitSoon();
      }
    });
    this._themeObs.observe(document.body, { attributes: true, attributeFilter: ['data-theme'] });

    // Match the two lines' widths (see _fitWidths) once fonts are in, and
    // again whenever the viewport or theme changes the metrics.
    this._fitWidths();
    document.fonts?.ready.then(() => this._fitWidths());
    document.fonts?.addEventListener?.('loadingdone', () => this._fitWidths());
    // belt and braces: the variable faces can swap in after `ready` resolves
    setTimeout(() => this._fitWidths(), 1500);
    this._onResize = () => this._fitSoon(80);
    window.addEventListener('resize', this._onResize, { passive: true });

    if (!this._coarse) {
      this._raf = requestAnimationFrame(this._tick);
      // A world (the game) covers the hero: stop chasing the scroll until it closes.
      this._onWorldOpen = () => { cancelAnimationFrame(this._raf); this._raf = 0; };
      this._onWorldClose = () => { if (!this._raf) this._raf = requestAnimationFrame(this._tick); };
      document.addEventListener('world:open', this._onWorldOpen);
      document.addEventListener('world:close', this._onWorldClose);
    }
  }

  // ── boot ────────────────────────────────────────────────────

  /**
   * Replace each visible word's text node with one span per character.
   *
   * A11y note: splitting "Sean" into <span>S</span><span>e</span>... causes
   * screen readers to announce "S e a n" (each inline-block becomes its own
   * AT word boundary). To preserve the accessible name, we hide the visual
   * wrapper from AT and insert a visually-hidden sibling holding the
   * original text. The .name__ghost wrapper already has this treatment in
   * the source HTML; we apply the same pattern to .name__solid here.
   */
  _split() {
    const wrappers = this.root.querySelectorAll('.name__solid, .name__ghost');

    wrappers.forEach((wrapper) => {
      const text = wrapper.textContent;

      // Hide the visual wrapper from AT (no-op if it's already aria-hidden).
      if (wrapper.getAttribute('aria-hidden') !== 'true') {
        wrapper.setAttribute('aria-hidden', 'true');
        // Add a visually-hidden sibling carrying the original word so the
        // accessible name stays intact. Skip if one already exists adjacent.
        const prev = wrapper.previousElementSibling;
        const alreadyHasA11ySibling =
          prev?.classList.contains('visually-hidden') &&
          prev?.textContent.trim() === text.trim();
        if (!alreadyHasA11ySibling) {
          const sr = document.createElement('span');
          sr.className = 'visually-hidden';
          sr.textContent = text;
          wrapper.parentNode.insertBefore(sr, wrapper);
        }
      }

      wrapper.textContent = '';
      for (const ch of text) {
        const span = document.createElement('span');
        span.className = 'name__char';
        span.textContent = ch;
        // Prime the transform so the first paint matches the first tick.
        span.style.transform = 'translate3d(0, 0, 0)';
        wrapper.appendChild(span);
        this._chars.push(span);
      }
    });
  }

  // ── per-letter poster styling ───────────────────────────────
  //
  // Concert-poster typography: every letter of each word gets its own weight,
  // width, size and baseline from a SHAPE (a curve over the word) chosen per
  // theme. Shapes are deterministic and balanced (they sum to ~0) so a word
  // never drifts fat or thin overall, and the two lines always use different
  // shapes. Amplitudes are deliberately large — "a little crazy is okay".
  //
  //   solid line:  Anybody  wdth 50–150, wght 100–900
  //   ghost line:  Archivo  wdth 62–125, wght 100–900
  //
  // Override per theme with `--name-style: <solidShape> <ghostShape>` (see
  // SHAPES for names) in _themes.scss; otherwise the theme name picks.

  _styleLetters() {
    this.root.classList.toggle('is-poster', this._armed);
    if (!this._armed) {
      // neutral: clear every per-letter override so the wrapper's --vf-* applies
      for (const span of this._chars) {
        ['--wd', '--wg', '--sk'].forEach((v) => span.style.removeProperty(v));
        span.dataset.sk = '0';
      }
      return;
    }
    const theme = document.body.dataset.theme || 'clean';
    const css = getComputedStyle(document.body).getPropertyValue('--name-style').trim();
    const names = Object.keys(SHAPES);
    const hash = [...theme].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7);
    let solidShape = names[hash % names.length];
    let ghostShape = names[(hash * 7 + 3) % names.length];
    if (css) {
      const [a, b] = css.split(/\s+/);
      if (SHAPES[a]) solidShape = a;
      if (SHAPES[b]) ghostShape = b;
    }
    if (ghostShape === solidShape) ghostShape = names[(names.indexOf(solidShape) + 2) % names.length];

    const lines = [
      // Only width, weight and a synthetic oblique vary — never size or
      // baseline — so every letter keeps the same cap height and the two lines
      // stay two clean rules, like a poster set on a grid. Floors keep the thin
      // end readable: below ~wght 300 / wdth 70 a display letter is a hairline.
      { sel: '.name__solid', shape: solidShape, wd: [72, 150, 104], wg: [320, 900, 600], sk: 9 },
      { sel: '.name__ghost', shape: ghostShape, wd: [66, 125, 82], wg: [280, 900, 500], sk: 11 },
    ];
    for (const L of lines) {
      const chars = this.root.querySelectorAll(`${L.sel} .name__char`);
      const n = chars.length;
      if (!n) continue;
      const shape = SHAPES[L.shape];
      // sample, then centre the curve so the word's mean stays on target
      const raw = [...chars].map((_, i) => shape(n === 1 ? 0.5 : i / (n - 1), i, n));
      const mean = raw.reduce((a, b) => a + b, 0) / n;
      const v = raw.map((r) => r - mean);
      const peak = Math.max(0.35, ...v.map(Math.abs));
      chars.forEach((span, i) => {
        const x = v[i] / peak; // −1 … 1
        const [wdMin, wdMax, wdMid] = L.wd;
        const [wgMin, wgMax, wgMid] = L.wg;
        const wd = x >= 0 ? wdMid + (wdMax - wdMid) * x : wdMid + (wdMid - wdMin) * x;
        const wg = x >= 0 ? wgMid + (wgMax - wgMid) * x : wgMid + (wgMid - wgMin) * x;
        // the light letters lean (synthetic oblique), the heavy ones stand square
        const sk = x < 0 ? -x * L.sk : 0;
        span.style.setProperty('--wd', Math.round(wd));
        span.style.setProperty('--wg', Math.round(wg));
        span.dataset.sk = sk.toFixed(1); // deg — applied in the scroll tick (transform is owned there)
      });
    }
    if (this._coarse) this._paintStill();
  }

  // Coarse pointers don't chase scroll. Poster skew still has to land once.
  _paintStill() {
    for (const span of this._chars) {
      const sk = parseFloat(span.dataset.sk) || 0;
      span.style.transform = sk ? `skewX(${-sk}deg)` : '';
    }
  }

  // ── width fit ───────────────────────────────────────────────
  //
  // "Sean" is four wide letters, "Burles" six condensed ones — left alone the
  // lines land at different widths. Measure both and tune the last name's
  // letter-spacing (it's a plain CSS property, so the theme morph still
  // animates it) until it is the same width as the first name, or a touch
  // longer. If tracking alone can't get there (theme with a very narrow
  // ghost face) the width axis of the whole line is nudged as well.

  _fitSoon(ms) {
    clearTimeout(this._fitTimer);
    // after a theme change wait for the font-variation morph to settle
    const raw = getComputedStyle(document.body).getPropertyValue('--morph-dur').trim();
    const dur = raw.endsWith('ms') ? parseFloat(raw) : parseFloat(raw) * 1000;
    const morph = ms ?? ((Number.isFinite(dur) ? dur : 700) + 80);
    this._fitTimer = setTimeout(() => this._fitWidths(), morph);
  }

  _fitKey() {
    return `${document.body.dataset.theme || 'clean'}|${this._armed ? 'poster' : 'plain'}|${window.innerWidth}`;
  }

  _fitWidths() {
    const solid = this.root.querySelector('.name__solid');
    const ghost = this.root.querySelector('.name__ghost');
    if (!solid || !ghost) return;
    const n = ghost.querySelectorAll('.name__char').length || 1;

    // measure with transitions off — otherwise we'd read the mid-morph value
    // of whatever letter-spacing / axis tween is still running
    this.root.classList.add('is-measuring');
    const prevLs = ghost.style.letterSpacing;
    const prevW = ghost.style.getPropertyValue('--fit-wdth');
    ghost.style.letterSpacing = '';
    ghost.style.removeProperty('--fit-wdth');
    void this.root.offsetWidth; // reflow so the reset is what we measure
    const target = solid.getBoundingClientRect().width * 1.03; // Burles may run a hair long
    const w = ghost.getBoundingClientRect().width;
    const fs = parseFloat(getComputedStyle(ghost).fontSize) || 100;
    const base = parseFloat(getComputedStyle(ghost).letterSpacing) || 0;

    // letter-spacing applies after every letter (including the last), so the
    // line grows by n × delta
    let delta = (target - w) / n;
    const min = -0.04 * fs, max = 0.28 * fs;
    const clamped = Math.max(min, Math.min(max, delta));
    const ls = `${((base + clamped) / fs).toFixed(4)}em`;
    let wdth = '';
    if (delta > max) {
      // still short: widen the whole line on the wdth axis (Archivo tops out at 125)
      ghost.style.letterSpacing = ls;
      void this.root.offsetWidth;
      const after = ghost.getBoundingClientRect().width;
      wdth = Math.min(1.5, target / Math.max(1, after)).toFixed(3);
    }
    // put the previous values back while still frozen, THEN re-enable
    // transitions and apply — so the adjustment animates instead of snapping
    ghost.style.letterSpacing = prevLs;
    if (prevW) ghost.style.setProperty('--fit-wdth', prevW); else ghost.style.removeProperty('--fit-wdth');
    void this.root.offsetWidth;
    this.root.classList.remove('is-measuring');
    ghost.style.letterSpacing = ls;
    if (wdth) ghost.style.setProperty('--fit-wdth', wdth); else ghost.style.removeProperty('--fit-wdth');
    (this._fitCache ||= {})[this._fitKey()] = { ls, wdth };
  }

  _bindScroll() {
    if (window.lenis?.on) {
      this._lenisOff = window.lenis.on('scroll', ({ scroll = 0 } = {}) => {
        this._scrollTarget = scroll;
      });
    } else {
      // Lenis fallback — native scroll. Less smooth but functionally identical.
      this._onNativeScroll = () => { this._scrollTarget = window.scrollY || 0; };
      window.addEventListener('scroll', this._onNativeScroll, { passive: true });
    }
  }

  // ── render tick ─────────────────────────────────────────────

  _tick = () => {
    this._raf = requestAnimationFrame(this._tick);
    const target = this._scrollTarget;
    const max = CHAR_LEAD_MAX_PX;

    for (let i = 0; i < this._chars.length; i++) {
      const factor = CHAR_LEAD_FACTORS[i] ?? 0.40;
      // Negative offset = letter moves UP (opposite of scroll-down direction).
      let offset = -target * factor;
      if (offset > max) offset = max;
      else if (offset < -max) offset = -max;
      const span = this._chars[i];
      const sk = parseFloat(span.dataset.sk) || 0;
      span.style.transform = `translate3d(0, ${offset.toFixed(2)}px, 0)${sk ? ` skewX(${-sk}deg)` : ''}`;
    }
  };

  // ── teardown ────────────────────────────────────────────────

  destroy() {
    cancelAnimationFrame(this._raf);
    if (this._onWorldOpen) document.removeEventListener('world:open', this._onWorldOpen);
    if (this._onWorldClose) document.removeEventListener('world:close', this._onWorldClose);
    this._themeObs?.disconnect();
    clearTimeout(this._fitTimer);
    if (this._onResize) window.removeEventListener('resize', this._onResize);
    this._lenisOff?.();
    if (this._onNativeScroll) {
      window.removeEventListener('scroll', this._onNativeScroll);
    }
  }
}
